import { SECURITY_EVENT_TYPES } from '../constants';
import logger from '../logger';

/**
 * Memory Integrity Detector
 * 
 * Monitors memory usage patterns, detects memory corruption,
 * and identifies potential memory-based attacks.
 */
export class MemoryIntegrityDetector {
    constructor(onSecurityEvent) {
        this.onSecurityEvent = onSecurityEvent;
        this.initialized = false;
        this.memoryBaseline = null;
        this.memoryHistory = [];
        this.suspiciousAllocations = [];
        this.performanceObserver = null;
        this.lastMemoryCheck = Date.now();
    }

    /**
     * Initialize the memory integrity detector
     */
    async initialize() {
        try {
            logger.detector('Memory Integrity', 'initializing');

            // Create memory baseline
            await this.createMemoryBaseline();

            // Set up memory monitoring
            this.setupMemoryMonitoring();

            // Initialize performance monitoring if available
            this.setupPerformanceMonitoring();

            this.initialized = true;
            logger.detector('Memory Integrity', 'initialized');

        } catch (error) {
            logger.detector('Memory Integrity', 'failed', error);
            throw error;
        }
    }

    /**
     * Create baseline memory usage profile
     */
    async createMemoryBaseline() {
        try {
            const memoryInfo = this.getMemoryInfo();
            
            this.memoryBaseline = {
                ...memoryInfo,
                timestamp: Date.now(),
                objectCount: this.estimateObjectCount(),
                functionCount: this.estimateFunctionCount()
            };
            
            logger.verbose('Memory baseline created:', this.memoryBaseline);
            
        } catch (error) {
            console.warn('RASP - Failed to create memory baseline:', error);
        }
    }

    /**
     * Set up memory monitoring
     */
    setupMemoryMonitoring() {
        // Monitor global object growth
        this.monitorGlobalObjectGrowth();
        
        // Monitor function creation
        this.monitorFunctionCreation();
        
        // Set up periodic memory checks
        this.setupPeriodicChecks();
    }

    /**
     * Set up performance monitoring if available
     */
    setupPerformanceMonitoring() {
        try {
            // Check if Performance Observer is available (mainly for web)
            if (typeof PerformanceObserver !== 'undefined') {
                this.performanceObserver = new PerformanceObserver((list) => {
                    this.analyzePerformanceEntries(list.getEntries());
                });
                
                // Observe memory-related performance entries
                this.performanceObserver.observe({ 
                    entryTypes: ['measure', 'navigation', 'resource'] 
                });
            }
        } catch (error) {
            console.warn('RASP - Performance monitoring not available:', error);
        }
    }

    /**
     * Monitor global object growth
     */
    monitorGlobalObjectGrowth() {
        try {
            const globalObj = global || window || {};
            const initialKeys = Object.keys(globalObj);
            
            // Store initial global count
            this.initialGlobalCount = initialKeys.length;
            
            // Set up periodic global object monitoring
            setInterval(() => {
                this.checkGlobalObjectGrowth();
            }, 30000); // Check every 30 seconds
            
        } catch (error) {
            console.warn('RASP - Failed to setup global object monitoring:', error);
        }
    }

    /**
     * Monitor function creation
     */
    monitorFunctionCreation() {
        try {
            // Override Function constructor to detect dynamic function creation
            const originalFunction = Function;
            const self = this;
            
            global.Function = function(...args) {
                self.reportSuspiciousAllocation('dynamic_function_creation', {
                    args: args.slice(0, 2), // Don't log full function body for security
                    stackTrace: new Error().stack?.split('\n').slice(0, 5)
                });
                
                return originalFunction.apply(this, args);
            };
            
            // Preserve original properties
            Object.setPrototypeOf(global.Function, originalFunction);
            Object.defineProperty(global.Function, 'prototype', {
                value: originalFunction.prototype,
                writable: false
            });
            
        } catch (error) {
            console.warn('RASP - Failed to setup function monitoring:', error);
        }
    }

    /**
     * Set up periodic memory checks
     */
    setupPeriodicChecks() {
        setInterval(() => {
            this.performMemoryIntegrityCheck();
        }, 60000); // Check every minute
    }

    /**
     * Perform memory integrity check
     */
    async performCheck() {
        if (!this.initialized) return;

        try {
            await this.performMemoryIntegrityCheck();
        } catch (error) {
            console.warn('RASP - Memory integrity check failed:', error);
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.MEMORY_MODIFICATION, {
                message: 'Memory integrity check failed',
                details: { error: error.message }
            });
        }
    }

    /**
     * Perform comprehensive memory integrity check
     */
    async performMemoryIntegrityCheck() {
        const now = Date.now();
        
        try {
            // Get current memory info
            const currentMemory = this.getMemoryInfo();
            
            // Add to history
            this.memoryHistory.push({
                ...currentMemory,
                timestamp: now,
                objectCount: this.estimateObjectCount(),
                functionCount: this.estimateFunctionCount()
            });
            
            // Keep only recent history (last 100 entries)
            if (this.memoryHistory.length > 100) {
                this.memoryHistory = this.memoryHistory.slice(-100);
            }
            
            // Analyze memory patterns
            await this.analyzeMemoryPatterns(currentMemory);
            
            // Check for memory leaks
            await this.checkMemoryLeaks();
            
            // Check for suspicious allocations
            await this.analyzeSuspiciousAllocations();
            
            this.lastMemoryCheck = now;
            
        } catch (error) {
            console.warn('RASP - Memory integrity check failed:', error);
        }
    }

    /**
     * Get memory information
     */
    getMemoryInfo() {
        const memoryInfo = {
            timestamp: Date.now()
        };

        try {
            // Try to get performance memory info (Chrome/Edge/WebView)
            if (typeof performance !== 'undefined' && performance.memory) {
                memoryInfo.usedJSHeapSize = performance.memory.usedJSHeapSize;
                memoryInfo.totalJSHeapSize = performance.memory.totalJSHeapSize;
                memoryInfo.jsHeapSizeLimit = performance.memory.jsHeapSizeLimit;
            }

            // Try to get process memory info (Node.js) - only if available
            if (typeof process !== 'undefined' && process.memoryUsage) {
                const usage = process.memoryUsage();
                memoryInfo.rss = usage.rss;
                memoryInfo.heapUsed = usage.heapUsed;
                memoryInfo.heapTotal = usage.heapTotal;
                memoryInfo.external = usage.external;
            }

            // If no memory info available, use fallback estimation
            if (!memoryInfo.usedJSHeapSize && !memoryInfo.heapUsed) {
                // Estimate memory usage based on object count
                memoryInfo.estimatedMemory = this.estimateObjectCount() * 1000; // Rough estimation
            }

        } catch (error) {
            // Silently handle memory info errors in React Native
            memoryInfo.error = 'Memory info not available in this environment';
        }

        return memoryInfo;
    }

    /**
     * Estimate object count in memory
     */
    estimateObjectCount() {
        try {
            const globalObj = global || window || {};
            let objectCount = 0;
            
            // Count global objects
            Object.keys(globalObj).forEach(key => {
                try {
                    if (typeof globalObj[key] === 'object' && globalObj[key] !== null) {
                        objectCount++;
                    }
                } catch (e) {
                    // Ignore access errors
                }
            });
            
            return objectCount;
        } catch (error) {
            return 0;
        }
    }

    /**
     * Estimate function count in memory
     */
    estimateFunctionCount() {
        try {
            const globalObj = global || window || {};
            let functionCount = 0;
            
            // Count global functions
            Object.keys(globalObj).forEach(key => {
                try {
                    if (typeof globalObj[key] === 'function') {
                        functionCount++;
                    }
                } catch (e) {
                    // Ignore access errors
                }
            });
            
            return functionCount;
        } catch (error) {
            return 0;
        }
    }

    /**
     * Analyze memory usage patterns
     */
    async analyzeMemoryPatterns(currentMemory) {
        if (!this.memoryBaseline || this.memoryHistory.length < 5) return;

        try {
            // Check for rapid memory growth (MUCH LESS AGGRESSIVE)
            const recentHistory = this.memoryHistory.slice(-10);
            const memoryGrowth = this.calculateMemoryGrowth(recentHistory);

            if (memoryGrowth.rate > 5.0) { // Increased from 50% to 500% growth
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.MEMORY_MODIFICATION, {
                    message: 'Extreme memory growth detected',
                    details: {
                        growthRate: memoryGrowth.rate,
                        growthAmount: memoryGrowth.amount,
                        timeWindow: memoryGrowth.timeWindow
                    }
                });
            }
            
            // Check for unusual object/function count increases
            const baselineObjects = this.memoryBaseline.objectCount || 0;
            const currentObjects = currentMemory.objectCount || 0;
            const objectGrowth = currentObjects - baselineObjects;

            if (objectGrowth > 10000) { // Increased from 1000 to 10000 new objects
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.MEMORY_MODIFICATION, {
                    message: 'Extreme object count increase detected',
                    details: {
                        baseline: baselineObjects,
                        current: currentObjects,
                        growth: objectGrowth
                    }
                });
            }
            
        } catch (error) {
            console.warn('RASP - Memory pattern analysis failed:', error);
        }
    }

    /**
     * Calculate memory growth rate
     */
    calculateMemoryGrowth(history) {
        if (history.length < 2) return { rate: 0, amount: 0, timeWindow: 0 };

        const first = history[0];
        const last = history[history.length - 1];
        const timeWindow = last.timestamp - first.timestamp;
        
        // Use heap size if available, otherwise use object count
        const firstSize = first.usedJSHeapSize || first.heapUsed || first.objectCount || 0;
        const lastSize = last.usedJSHeapSize || last.heapUsed || last.objectCount || 0;
        
        const amount = lastSize - firstSize;
        const rate = firstSize > 0 ? amount / firstSize : 0;
        
        return { rate, amount, timeWindow };
    }

    /**
     * Check for memory leaks
     */
    async checkMemoryLeaks() {
        if (this.memoryHistory.length < 20) return;

        try {
            // Look for consistent memory growth over time
            const recentHistory = this.memoryHistory.slice(-20);
            let consistentGrowth = 0;
            
            for (let i = 1; i < recentHistory.length; i++) {
                const prev = recentHistory[i - 1];
                const curr = recentHistory[i];
                
                const prevSize = prev.usedJSHeapSize || prev.heapUsed || prev.objectCount || 0;
                const currSize = curr.usedJSHeapSize || curr.heapUsed || curr.objectCount || 0;
                
                if (currSize > prevSize) {
                    consistentGrowth++;
                }
            }
            
            // If memory grew in more than 80% of checks, it might be a leak
            if (consistentGrowth > recentHistory.length * 0.8) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.MEMORY_MODIFICATION, {
                    message: 'Potential memory leak detected',
                    details: {
                        consistentGrowthChecks: consistentGrowth,
                        totalChecks: recentHistory.length - 1,
                        growthPercentage: (consistentGrowth / (recentHistory.length - 1) * 100).toFixed(2)
                    }
                });
            }
            
        } catch (error) {
            console.warn('RASP - Memory leak check failed:', error);
        }
    }

    /**
     * Check global object growth
     */
    checkGlobalObjectGrowth() {
        try {
            const globalObj = global || window || {};
            const currentKeys = Object.keys(globalObj);
            const currentCount = currentKeys.length;
            const growth = currentCount - this.initialGlobalCount;
            
            // Alert if global object count increased significantly
            if (growth > 50) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.MEMORY_MODIFICATION, {
                    message: 'Significant global object growth detected',
                    details: {
                        initial: this.initialGlobalCount,
                        current: currentCount,
                        growth: growth
                    }
                });
            }
            
        } catch (error) {
            console.warn('RASP - Global object growth check failed:', error);
        }
    }

    /**
     * Analyze suspicious allocations
     */
    async analyzeSuspiciousAllocations() {
        if (this.suspiciousAllocations.length === 0) return;

        try {
            // Group allocations by type
            const allocationTypes = {};
            this.suspiciousAllocations.forEach(allocation => {
                const type = allocation.type;
                if (!allocationTypes[type]) {
                    allocationTypes[type] = [];
                }
                allocationTypes[type].push(allocation);
            });

            // Report if we have multiple suspicious allocations of the same type
            Object.entries(allocationTypes).forEach(([type, allocations]) => {
                if (allocations.length > 5) {
                    this.reportSecurityEvent(SECURITY_EVENT_TYPES.MEMORY_MODIFICATION, {
                        message: `Multiple suspicious ${type} allocations detected`,
                        details: {
                            type,
                            count: allocations.length,
                            recentAllocations: allocations.slice(-3)
                        }
                    });
                }
            });

            // Clear old allocations (keep only last 50)
            this.suspiciousAllocations = this.suspiciousAllocations.slice(-50);
            
        } catch (error) {
            console.warn('RASP - Suspicious allocation analysis failed:', error);
        }
    }

    /**
     * Report suspicious allocation
     */
    reportSuspiciousAllocation(type, details) {
        this.suspiciousAllocations.push({
            type,
            details,
            timestamp: Date.now()
        });
    }

    /**
     * Analyze performance entries
     */
    analyzePerformanceEntries(entries) {
        try {
            entries.forEach(entry => {
                // Look for unusually long operations that might indicate attacks
                if (entry.duration > 1000) { // More than 1 second
                    this.reportSecurityEvent(SECURITY_EVENT_TYPES.MEMORY_MODIFICATION, {
                        message: 'Unusually long operation detected',
                        details: {
                            name: entry.name,
                            duration: entry.duration,
                            entryType: entry.entryType
                        }
                    });
                }
            });
        } catch (error) {
            console.warn('RASP - Performance entry analysis failed:', error);
        }
    }

    /**
     * Report a security event
     */
    reportSecurityEvent(eventType, eventData) {
        if (this.onSecurityEvent) {
            this.onSecurityEvent(eventType, eventData);
        }
    }

    /**
     * Get memory statistics
     */
    getStatistics() {
        return {
            baseline: this.memoryBaseline,
            current: this.getMemoryInfo(),
            historyLength: this.memoryHistory.length,
            suspiciousAllocations: this.suspiciousAllocations.length,
            lastCheck: this.lastMemoryCheck
        };
    }

    /**
     * Cleanup the detector
     */
    cleanup() {
        try {
            // Disconnect performance observer
            if (this.performanceObserver) {
                this.performanceObserver.disconnect();
                this.performanceObserver = null;
            }

            // Clear data
            this.memoryHistory = [];
            this.suspiciousAllocations = [];
            this.initialized = false;
            
            console.log('RASP - Memory Integrity Detector cleanup completed');
        } catch (error) {
            console.error('RASP - Memory Integrity Detector cleanup failed:', error);
        }
    }
}
